/**
 * GroupAvatar — circular composite avatar for group conversations.
 * Uses the custom group photo when one exists, otherwise tiles up to four
 * members (photo, or their initial) the way mobile's GroupAvatar does.
 */
import { useEffect, useState } from 'react';
import { selectCollageMembers } from '@wlb/shared/utils/groupConversation';
import type { ConversationMember } from '@wlb/shared/types/chat';
import { avatarFallback } from '../../components/ui/avatarFallback';
import { GroupsTabIcon } from '../../components/ui/icons';
import { resolveAvatarSrc } from './avatarSource';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import '../../components/ui/avatar.css';
import './messages.css';

interface GroupAvatarProps {
  name: string;
  avatar?: unknown;
  members?: ConversationMember[];
  /** Newest-first sender ids; leads the tile order when messages are loaded. */
  recentSenderIds?: Array<string | number>;
  variant?: 'list' | 'header' | 'details';
}

function CollageCell({ member }: { member: ConversationMember }) {
  const src = resolveAvatarSrc(member.avatar);
  const { initial, tone } = avatarFallback({
    seed: member.profileUserGuid ?? member.id,
    name: getUsernameDisplay(member),
    username: member.username,
  });
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    return (
      <img
        className="group-avatar__cell group-avatar__image"
        src={src}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="group-avatar__cell group-avatar__initial" data-avatar-tone={tone} aria-hidden>
      {initial}
    </span>
  );
}

export function GroupAvatar({
  avatar,
  members = [],
  recentSenderIds,
  variant = 'list',
}: GroupAvatarProps) {
  const groupPhoto = resolveAvatarSrc(avatar);
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const showGroupPhoto = Boolean(groupPhoto && failedPhoto !== groupPhoto);
  const tiles = showGroupPhoto ? [] : selectCollageMembers(members, recentSenderIds);
  const count = showGroupPhoto ? 1 : Math.max(tiles.length, 1);

  return (
    <div className={`group-avatar group-avatar--${variant} group-avatar--count-${count}`}>
      {showGroupPhoto ? (
        <img
          className="group-avatar__cell group-avatar__image"
          src={groupPhoto ?? undefined}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          onError={() => setFailedPhoto(groupPhoto)}
        />
      ) : tiles.length > 0 ? (
        tiles.map(member => <CollageCell key={member.id} member={member} />)
      ) : (
        <span className="group-avatar__cell group-avatar__initial" aria-hidden>
          <GroupsTabIcon size={variant === 'details' ? 28 : 20} />
        </span>
      )}
    </div>
  );
}
